<template>
  <form class="flex flex-col gap-y-4 font-Spectral">
    <div class="flex justify-between gap-[2%]">
      <FloatingLabelTextInput
        aria-label="firstName"
        autocomplete="given-name"
        label="First name"
        :error="errorMessages.firstName"
        :model-value="form.step2.firstName"
        :on-update-model-value="updateField.firstName"
      />
      <FloatingLabelTextInput
        aria-label="lastName"
        autocomplete="family-name"
        label="Last name"
        :error="errorMessages.lastName"
        :model-value="form.step2.lastName"
        :on-update-model-value="updateField.lastName"
      />
    </div>
    <FloatingLabelTextInput
      aria-label="email"
      autocomplete="email"
      label="Email"
      type="email"
      :error="errorMessages.email"
      :model-value="form.step2.email"
      :on-update-model-value="updateField.email"
    />
    <div>
      <!-- 開いたら会社名か参加者のどちらかが要る。会社名だけなら予約者本人が参加者になる（D-036） -->
      <button
        aria-controls="someone-else-fields"
        class="flex w-full items-center gap-x-2 py-1 text-left text-sm text-gold1"
        type="button"
        :aria-expanded="isSomeoneElseFormOpen"
        @click="onToggleSomeoneElseForm"
      >
        Booking for a company or someone else?<span aria-hidden="true">{{ disclosureMark }}</span>
      </button>
      <div v-show="isSomeoneElseFormOpen" id="someone-else-fields" class="mt-4 flex flex-col gap-y-4">
        <FloatingLabelTextInput
          aria-label="companyName"
          autocomplete="organization"
          label="Company name"
          :model-value="form.step2.companyName"
          :on-update-model-value="updateField.companyName"
          :required="false"
        />
        <p class="text-sm text-light-fg-muted">If the participant is someone other than you, please enter the participant's contact details.</p>
        <div class="flex justify-between gap-[2%]">
          <FloatingLabelTextInput
            aria-label="participantFirstName"
            autocomplete="section-participant given-name"
            label="First name"
            :error="errorMessages.participantFirstName"
            :model-value="form.step2.participantFirstName"
            :on-update-model-value="updateField.participantFirstName"
            :required="form.step2.shouldShowSomeoneElseForm"
          />
          <FloatingLabelTextInput
            aria-label="participantLastName"
            autocomplete="section-participant family-name"
            label="Last name"
            :error="errorMessages.participantLastName"
            :model-value="form.step2.participantLastName"
            :on-update-model-value="updateField.participantLastName"
            :required="form.step2.shouldShowSomeoneElseForm"
          />
        </div>
        <FloatingLabelTextInput
          aria-label="forSomeoneElseEmail"
          autocomplete="section-participant email"
          label="Email address"
          type="email"
          :error="errorMessages.forSomeoneElseEmail"
          :model-value="form.step2.forSomeoneElseEmail"
          :on-update-model-value="updateField.forSomeoneElseEmail"
          :required="form.step2.shouldShowSomeoneElseForm"
        />
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import FloatingLabelTextInput from '@/wabunka-src/common/components/stateful/FloatingLabelTextInput/FloatingLabelTextInput.vue';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import { isCompanyOrSomeoneElseFormOpen, requiresParticipantDetails } from '@/wabunka-src/common/features/Reservation/core/requiredFields/companyOrSomeoneElseForm';
import type { WabunkaReservationFormStep2 } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaReservations';

interface Props {
  getErrorMessage: (s: string) => string;
}

const props = defineProps<Props>();

const { form } = useGlobalWabunkaReservationForm();

const errorMessages = computed(() => ({
  firstName: props.getErrorMessage('step2.firstName'),
  lastName: props.getErrorMessage('step2.lastName'),
  email: props.getErrorMessage('step2.email'),
  participantFirstName: props.getErrorMessage('step2.participantFirstName'),
  participantLastName: props.getErrorMessage('step2.participantLastName'),
  forSomeoneElseEmail: props.getErrorMessage('step2.forSomeoneElseEmail'),
}));

// 開いている間の shouldShowSomeoneElseForm は参加者の欄を求めるかを表す。会社名だけなら立てず、旧動線の For myself ＋会社名と同じ形で送る
const updateSomeoneElseForm = (fields: Partial<Pick<WabunkaReservationFormStep2, 'companyName' | 'participantFirstName' | 'participantLastName' | 'forSomeoneElseEmail'>>) => {
  Object.assign(form.value.step2, fields);
  form.value.step2.shouldShowSomeoneElseForm = requiresParticipantDetails(form.value.step2);
};

const updateField = {
  firstName: (value: string) => (form.value.step2.firstName = value),
  lastName: (value: string) => (form.value.step2.lastName = value),
  email: (value: string) => (form.value.step2.email = value),
  companyName: (value: string) => updateSomeoneElseForm({ companyName: value }),
  participantFirstName: (value: string) => updateSomeoneElseForm({ participantFirstName: value }),
  participantLastName: (value: string) => updateSomeoneElseForm({ participantLastName: value }),
  forSomeoneElseEmail: (value: string) => updateSomeoneElseForm({ forSomeoneElseEmail: value }),
};

const isSomeoneElseFormOpen = computed(() => isCompanyOrSomeoneElseFormOpen(form.value.step2));

const disclosureMark = computed(() => (isSomeoneElseFormOpen.value ? '−' : '＋'));

// 会社名は開閉に関わらず送られるため、閉じるときに消す（参加者の欄は閉じていれば送信側で落ちる）。開いた直後は会社名が空なので参加者の欄を求める
const onToggleSomeoneElseForm = () => {
  const willOpen = !isSomeoneElseFormOpen.value;
  if (!willOpen) form.value.step2.companyName = '';
  form.value.step2.shouldShowSomeoneElseForm = willOpen;
};
</script>
